import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Box,
  IconButton,
  Input,
  InputAdornment,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Search } from '@material-ui/icons';
import ClearIcon from '@material-ui/icons/Clear';
import clsx from 'clsx';
import { useDebouncedCallback } from 'use-debounce';

const DEBOUNCE_DELAY_IN_MILLISECONDS = 1000;

type SearchBarProps = {
  autoFocus?: boolean;
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  size?: 'medium' | 'small';
  showSearchIcon?: boolean;
  debounceTimer?: number;
  onChange?: (value: string) => void;
  onCancelSearch?: () => void;
  endAdornment?: React.ReactNode;
};

const useStyles = makeStyles(
  ({ palette, spacing, transitions, typography }) => ({
    root: (props: { size: 'medium' | 'small' }) => ({
      height:
        props.size === 'small'
          ? typography.pxToRem(40)
          : typography.pxToRem(48),
      display: 'flex',
      justifyContent: 'space-between',
      backgroundColor: palette.background.tertiary,
      borderRadius: 8,
      border: `1px solid ${palette.divider}`,
      boxSizing: 'border-box',
    }),

    iconButton: {
      background: 'none !important',
      color: palette.action.active,
      transform: 'scale(1, 1)',
      transition: transitions.create(['transform', 'color'], {
        duration: transitions.duration.shorter,
        easing: transitions.easing.easeInOut,
      }),
      padding: spacing(1),
    },

    smallIconButton: {
      padding: spacing(0.5),
    },

    iconButtonHidden: {
      transform: 'scale(0, 0)',
      '& > $icon': {
        opacity: 0,
      },
    },

    searchIconButton: (props: { size: 'medium' | 'small' }) => ({
      width: props.size === 'small' ? spacing(2.5) : spacing(3),
      height: props.size === 'small' ? spacing(2.5) : spacing(3),
      color: palette.action.disabled,
      transform: 'scale(1, 1)',
      transition: transitions.create(['transform', 'color'], {
        duration: transitions.duration.shorter,
        easing: transitions.easing.easeInOut,
      }),
    }),

    icon: (props: { size: 'medium' | 'small' }) => ({
      transition: transitions.create(['opacity'], {
        duration: transitions.duration.shorter,
        easing: transitions.easing.easeInOut,
      }),
      fontSize: props.size === 'small' ? 18 : undefined,
    }),

    input: (props: { size: 'medium' | 'small' }) => ({
      fontSize:
        props.size === 'small'
          ? typography.pxToRem(12)
          : typography.pxToRem(14),
      '&::placeholder': {
        fontSize:
          props.size === 'small'
            ? typography.pxToRem(12)
            : typography.pxToRem(14),
      },
    }),

    searchContainer: (props: { size: 'medium' | 'small' }) => ({
      margin: props.size === 'small' ? 'auto 8px' : 'auto 16px',
      width:
        props.size === 'small'
          ? `calc(100% - ${spacing(4 + 2)}px)`
          : `calc(100% - ${spacing(6 + 4)}px)`,
    }),
  }),
);

export default function SearchBar(props: SearchBarProps) {
  const {
    autoFocus = true,
    placeholder = 'Procurar...',
    disabled,
    value,
    showSearchIcon = false,
    debounceTimer = DEBOUNCE_DELAY_IN_MILLISECONDS,
    onChange,
    onCancelSearch,
    endAdornment,
    size = 'small',
  } = props;

  const classes = useStyles({ size });
  const [inputValue, setInputValue] = useState(value || '');
  const previousValueRef = useRef<string>('');

  const debounced = useDebouncedCallback((currentValue: string) => {
    const trimmedValue = currentValue.trim();
    const previousValue = previousValueRef.current.trim();

    if (trimmedValue) {
      onChange?.(trimmedValue);
    } else if (previousValue) {
      onCancelSearch?.();
    }

    previousValueRef.current = currentValue;
  }, debounceTimer);

  const handleCancelSearch = useCallback(() => {
    setInputValue('');
    previousValueRef.current = '';
    onCancelSearch?.();
  }, [onCancelSearch]);

  const handleBlur = useCallback(() => {
    setInputValue(v => v?.trim());
  }, []);

  useEffect(() => {
    setInputValue(value || '');
    previousValueRef.current = value || '';
  }, [value]);

  return (
    <Paper elevation={0} className={classes.root}>
      <Box className={classes.searchContainer}>
        <Input
          fullWidth
          autoFocus={autoFocus}
          placeholder={placeholder}
          startAdornment={
            showSearchIcon &&
            !inputValue && (
              <InputAdornment position="start">
                <Search className={classes.searchIconButton} />
              </InputAdornment>
            )
          }
          onBlur={handleBlur}
          value={inputValue}
          onChange={event => {
            const newValue = event.target.value.trimStart();
            setInputValue(newValue);
            debounced(newValue);
          }}
          disableUnderline
          disabled={disabled}
          className={classes.input}
        />
      </Box>

      {endAdornment}

      <IconButton
        aria-label="Limpar busca"
        disabled={disabled}
        onClick={handleCancelSearch}
        className={clsx(
          classes.iconButton,
          size === 'small' && classes.smallIconButton,
          {
            [classes.iconButtonHidden]: inputValue === '',
          },
        )}
      >
        <ClearIcon className={classes.icon} />
      </IconButton>
    </Paper>
  );
}
